/**
 * StatusChip (TMMPDM-0065, TMM-B2) — the one status chip. The tone comes from the registry by
 * `status`, or is given as `tone` for a flag that has no status enum (a boolean the server sent:
 * "Can't sell", "In force"). The label is always text (STATE-002) and is the caller's, so the
 * catalog copy and its fallback stay where each status already has them.
 *
 * A status the registry doesn't know renders as a neutral chip. An outlined chip means draft and
 * nothing else (TMM profile 1.1, one label one tone), so the variant is not a prop: only the draft
 * tone is outlined. `domain` picks the tone a status has in one domain (an invitation's
 * `REVOKED` is "Removed", neutral). Anything else a `Chip` takes (an icon, a tooltip's focus
 * handlers, a router link) passes through. A neutral chip (ended, inactive, closed) is quiet
 * through a text role — never opacity (TMM-T4).
 */
import { Chip, type ChipProps } from '@mui/material';
import type { ElementType } from 'react';
import { statusTone, type StatusDomain, type Tone } from './tones';

type ChipColor = NonNullable<ChipProps['color']>;

const TONE_COLOR: Record<Tone, ChipColor> = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  neutral: 'default',
  draft: 'default',
};

export type StatusChipProps = Omit<ChipProps, 'color' | 'size' | 'label' | 'sx' | 'variant'> & {
  label: string;
  /** The server's status, looked up in the registry. */
  status?: string;
  /** The domain the status is read in, where its tone depends on its meaning there. */
  domain?: StatusDomain;
  /** A tone given outright, for a flag with no status enum. It wins over `status`. */
  tone?: Tone;
  /** A link chip (e.g. `RouterLink`). */
  component?: ElementType;
  to?: string;
};

export function StatusChip({ label, status, domain, tone, ...rest }: StatusChipProps) {
  const resolved = tone ?? (status === undefined ? undefined : statusTone(status, domain));
  const color = resolved === undefined ? 'default' : TONE_COLOR[resolved];
  return (
    <Chip
      size="small"
      label={label}
      color={color}
      variant={resolved === 'draft' ? 'outlined' : 'filled'}
      {...(resolved === 'neutral' || resolved === undefined
        ? { sx: { color: 'text.secondary' } }
        : {})}
      {...(rest as ChipProps)}
    />
  );
}
